import * as React from 'react';
import styled, { keyframes } from 'styled-components';

import { composeRefs } from '../../internals/composeRefs';
import type { FloatingAlign, FloatingSide } from '../../internals/computeFloatingPosition';
import { createUIContext } from '../../internals/createContext';
import { getSemanticAttributes } from '../../internals/semanticProps';
import { Portal } from '../../internals/Portal';
import { Slot } from '../../internals/Slot';
import { useClickOutside } from '../../internals/useClickOutside';
import { useControllable } from '../../internals/useControllable';
import { useEscapeKey } from '../../internals/useEscapeKey';
import { useFloatingPosition } from '../../internals/useFloatingPosition';
import { useId } from '../../internals/useId';
import { palette } from '../../tokens/palette';
import { rem } from '../../tokens/spacing';
import { zIndex } from '../../tokens/zIndex';

/* ─────────────────────────── Context ───────────────────────────────────── */

export interface PopoverRootProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: FloatingSide;
  align?: FloatingAlign;
  children: React.ReactNode;
}

interface PopoverContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerRef: React.MutableRefObject<HTMLElement | null>;
  contentRef: React.MutableRefObject<HTMLDivElement | null>;
  popoverId: string;
  triggerId: string;
  side: FloatingSide;
  align: FloatingAlign;
}

const [PopoverProvider, usePopoverContext] = createUIContext<PopoverContextValue>('Popover');

/* ─────────────────────────── Root ──────────────────────────────────────── */

const PopoverRoot = ({
  open: openProp,
  defaultOpen,
  onOpenChange,
  side = 'bottom',
  align = 'end',
  children,
}: PopoverRootProps) => {
  const [open, setOpen] = useControllable<boolean>({
    value: openProp,
    defaultValue: defaultOpen ?? false,
    onChange: onOpenChange,
  });
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const popoverId = useId('popover');
  const triggerId = useId('popover-trigger');

  const value = React.useMemo(
    () => ({ open, onOpenChange: setOpen, triggerRef, contentRef, popoverId, triggerId, side, align }),
    [open, setOpen, popoverId, triggerId, side, align],
  );

  return <PopoverProvider value={value}>{children}</PopoverProvider>;
};

PopoverRoot.displayName = 'Popover.Root';

/* ─────────────────────────── Trigger ──────────────────────────────────── */

export interface PopoverTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  semanticId?: string;
}

const PopoverTrigger = React.forwardRef<HTMLButtonElement, PopoverTriggerProps>(
  function PopoverTrigger({ asChild = false, semanticId, onClick, type = 'button', children, ...rest }, ref) {
    const { open, onOpenChange, triggerRef, popoverId, triggerId } = usePopoverContext();
    const semanticProps = getSemanticAttributes(semanticId);
    const composedRef = React.useMemo(() => composeRefs(ref, triggerRef), [ref, triggerRef]);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      if (!event.defaultPrevented) onOpenChange(!open);
    };

    if (asChild) {
      return (
        <Slot
          ref={composedRef as React.Ref<HTMLElement>}
          id={triggerId}
          aria-haspopup='dialog'
          aria-expanded={open}
          aria-controls={popoverId}
          data-state={open ? 'open' : 'closed'}
          onClick={handleClick as React.MouseEventHandler<HTMLElement>}
          {...semanticProps}
          {...(rest as unknown as React.ComponentProps<typeof Slot>)}
        >
          {children}
        </Slot>
      );
    }

    return (
      <button
        ref={composedRef as React.Ref<HTMLButtonElement>}
        id={triggerId}
        type={type}
        aria-haspopup='dialog'
        aria-expanded={open}
        aria-controls={popoverId}
        data-state={open ? 'open' : 'closed'}
        onClick={handleClick}
        {...semanticProps}
        {...rest}
      >
        {children}
      </button>
    );
  },
);

PopoverTrigger.displayName = 'Popover.Trigger';

/* ─────────────────────────── Content ──────────────────────────────────── */

const panelSlideIn = keyframes`
  from { opacity: 0; transform: translateY(${rem(-4)}); }
  to   { opacity: 1; transform: translateY(0); }
`;
const panelSlideOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to   { opacity: 0; transform: translateY(${rem(-4)}); }
`;

const StyledPanel = styled.div`
  position: fixed;
  z-index: ${zIndex.dropdown};
  background: var(--ui-surface, ${palette.White});
  color: var(--ui-text, ${palette.Black});
  border: 1px solid var(--ui-border, ${palette.CulturedWhite});
  border-radius: var(--ui-radius-lg, ${rem(12)});
  box-shadow: 0 ${rem(4)} ${rem(24)} var(--ui-shadow-default, rgba(109, 109, 109, 0.12));
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
  overflow: hidden;

  &[data-state='open'] {
    animation: ${panelSlideIn} 180ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }
  &[data-state='closed'] {
    animation: ${panelSlideOut} 150ms ease-in forwards;
  }
`;

export interface PopoverContentProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
}

const PopoverContent = React.forwardRef<HTMLDivElement, PopoverContentProps>(
  function PopoverContent({ semanticId, style, children, ...rest }, ref) {
    const { open, onOpenChange, triggerRef, contentRef, popoverId, triggerId, side, align } =
      usePopoverContext();
    const semanticProps = getSemanticAttributes(semanticId);

    const close = React.useCallback(() => onOpenChange(false), [onOpenChange]);

    useClickOutside(contentRef, close, open, { ignoredRefs: [triggerRef] });
    useEscapeKey(close, open);

    // Keep the panel mounted during exit animation so panelSlideOut can play.
    const [mounted, setMounted] = React.useState(false);
    const panelRef = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
      if (open) {
        setMounted(true);
      } else if (mounted) {
        // Wait for exit animation to finish, then unmount
        const el = panelRef.current;
        if (!el) { setMounted(false); return; }

        const onEnd = (e: AnimationEvent) => {
          if (e.target === el) setMounted(false);
        };
        el.addEventListener('animationend', onEnd);
        // Fallback: unmount after animation duration + buffer
        const fallback = window.setTimeout(() => setMounted(false), 200);
        return () => {
          el.removeEventListener('animationend', onEnd);
          window.clearTimeout(fallback);
        };
      }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const { coords } = useFloatingPosition({
      open: mounted,
      side,
      align,
      offsetPx: 8,
      triggerRef,
      floatingRef: contentRef,
    });

    const composedRef = React.useMemo(
      () => composeRefs(ref, contentRef, panelRef),
      [ref, contentRef],
    );

    if (!mounted) return null;

    // Panel is "positioned" once useFloatingPosition returns real coords.
    // Before that it's in the DOM for measurement but invisible (no animation).
    const isPositioned = coords.top !== -9999;
    const dataState = !isPositioned ? 'measuring' : open ? 'open' : 'closed';

    return (
      <Portal>
        <StyledPanel
          ref={composedRef}
          id={popoverId}
          role='dialog'
          aria-labelledby={triggerId}
          tabIndex={-1}
          data-state={dataState}
          data-positioned={String(isPositioned)}
          data-side={side}
          data-align={align}
          style={{
            top: coords.top,
            left: coords.left,
            ...(dataState === 'measuring' ? { opacity: 0, pointerEvents: 'none' as const } : {}),
            ...style,
          }}
          {...semanticProps}
          {...rest}
        >
          {children}
        </StyledPanel>
      </Portal>
    );
  },
);

PopoverContent.displayName = 'Popover.Content';

/* ─────────────────────────── Compound export ──────────────────────────── */

export const Popover = {
  Root: PopoverRoot,
  Trigger: PopoverTrigger,
  Content: PopoverContent,
};
